<script setup>
import AccountValueSection from 'components/market/AccountValueSection.vue';
import MarketReadout from 'components/market/MarketReadout.vue';
import MarketShareList from 'components/market/MarketShareList.vue';
import { usePageMeta } from 'composables/page-meta';
import { TECH_MARKET_CLASSIFICATION_SLUG } from 'src/configs/market';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { formatMarketCount } from 'src/utils/market-util';
import { buildPageMeta } from 'src/utils/page-meta-util';
import { trackProductEvent } from 'src/utils/umami-util';
import { buildXprtJobsCategoryUrl } from 'src/utils/xprt-jobs-route-util';
import { useAuthStore } from 'stores/auth-store';
import { useMarketStore } from 'stores/market-store';
import { computed } from 'vue';

// Reads as a sentence opener; the list is short because the heading only ever counts the leading specialisations.
const SPELLED_COUNTS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

const methodologyNotes = [
    'Coverage and active listings',
    'Advertised salary evidence',
    '30-day movement',
    'Advertising organisations',
    'Breakdowns and refresh',
    'Xprt Dev and Xprt Jobs',
    'Corrections'
];

defineOptions({
    async preFetch() {
        const marketStore = useMarketStore();
        try {
            await marketStore.loadOverview();
        } catch {
            // The landing explanation remains useful when the current snapshot is temporarily unavailable.
        }
    }
});

const authStore = useAuthStore();
const marketStore = useMarketStore();

const heroMarketWatchRoute = computed(() => {
    return AppRoute.build(authStore.userAuthenticated ? Route.Dashboard : Route.Login);
});

const heroMarketWatchLabel = computed(() => {
    return authStore.userAuthenticated ? 'Open My Market' : 'Save one market';
});

const careerProfileRoute = computed(() => {
    if (!authStore.userAuthenticated) {
        return AppRoute.build(Route.Login);
    }
    return AppRoute.build(authStore.userReady ? Route.ManageUserProfile : Route.Setup);
});

const specialisations = computed(() => {
    return Array.isArray(marketStore.overview?.subclassifications) ? marketStore.overview.subclassifications : [];
});

const leadingSpecialisations = computed(() => {
    return [...specialisations.value]
        .sort((first, second) => second.jobCount - first.jobCount)
        .slice(0, 6)
        .map(specialisation => ({
            label: specialisation.name,
            value: specialisation.jobCount,
            detail: formatMarketCount(specialisation.jobCount, 'active role'),
            href: `/market/${specialisation.slug}`
        }));
});

// The heading states the share these specialisations actually hold, rather than asserting that they lead the market
// and leaving the claim to drift with the data or with the number of rows shown.
const leadingSharePercent = computed(() => {
    const total = specialisations.value.reduce((sum, item) => sum + (item.jobCount || 0), 0);
    if (!total) {
        return null;
    }
    const leading = leadingSpecialisations.value.reduce((sum, item) => sum + (item.value || 0), 0);
    return Math.round((leading / total) * 100);
});

const demandHeading = computed(() => {
    if (!leadingSharePercent.value) {
        return 'Where the demand sits across ICT.';
    }
    const rows = leadingSpecialisations.value.length;
    const count = SPELLED_COUNTS[rows] ?? rows;
    return rows === 1
        ? `${count} specialisation carries ${leadingSharePercent.value}% of active listings.`
        : `${count} specialisations carry ${leadingSharePercent.value}% of active listings.`;
});

const allSpecialisationsLabel = computed(() => {
    return specialisations.value.length > 0
        ? `See all ${specialisations.value.length} ICT specialisations`
        : 'See all ICT specialisations';
});

function trackMarketOverview(component) {
    trackProductEvent('market_overview_opened', {
        component,
        route: '/market'
    });
}

function trackMarketWatchOpen() {
    trackProductEvent('market_watch_opened', {
        component: 'landing_hero',
        route: authStore.userAuthenticated ? '/app' : '/app/login'
    });
}

function trackJobsHandoff() {
    trackProductEvent('xprt_jobs_handoff', {
        component: 'landing',
        classificationSlug: TECH_MARKET_CLASSIFICATION_SLUG,
        locationSlug: 'australia'
    });
}

function trackCareerProfileOpen() {
    trackProductEvent('career_profile_opened', {
        component: 'landing',
        route: !authStore.userAuthenticated ? '/app/login' : (authStore.userReady ? '/app/profile' : '/app/setup')
    });
}

usePageMeta(buildPageMeta({
    title: 'Australian Tech Job Market Insights | Xprt Dev',
    description: 'Explore Australian tech job demand, advertised salaries and hiring employers from Xprt Jobs listings. '
        + 'Compare ICT specialisations and states.',
    canonicalUrl: 'https://xprt.dev/',
    isHomePage: true
}));
</script>

<template>
    <main class="landing-page">
        <header class="market-hero">
            <div class="hero-grid container-md q-px-md q-py-xl">
                <div class="hero-copy">
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-md">
                        Australia · Information &amp; communication technology · Active listings
                    </p>
                    <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">
                        What Australian tech roles pay, and who is hiring.
                    </h1>
                    <p class="hero-deck text-h6 text-weight-regular text-hint q-mt-none q-mb-lg">
                        Demand, advertised salaries, work arrangements and the organisations advertising for every ICT
                        specialisation, measured from the roles that are live on Xprt Jobs today.
                    </p>
                    <div class="row q-gutter-sm">
                        <q-btn
                            :to="AppRoute.build(Route.MarketOverview)"
                            color="primary"
                            data-test="hero-market-link"
                            icon-right="sym_r_arrow_forward"
                            label="Explore the market"
                            no-caps
                            unelevated
                            @click="trackMarketOverview('landing_hero')"
                        />
                        <q-btn
                            :label="heroMarketWatchLabel"
                            :to="heroMarketWatchRoute"
                            color="primary"
                            data-test="hero-market-watch-link"
                            no-caps
                            outline
                            @click="trackMarketWatchOpen"
                        />
                    </div>
                </div>
                <MarketReadout :snapshot="marketStore.overview"/>
            </div>
        </header>
        <section class="market-section-rule" aria-labelledby="landing-demand-heading">
            <div class="demand-grid container-md q-px-md q-py-xl">
                <div>
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Where the demand is</p>
                    <h2 id="landing-demand-heading" class="text-h4 text-weight-bold q-mt-none q-mb-md">
                        {{ demandHeading }}
                    </h2>
                    <p class="text-body1 text-hint q-mt-none q-mb-lg">
                        Each one has its own page with advertised pay, work arrangements and the organisations hiring for
                        it. The full market page ranks every specialisation.
                    </p>
                    <q-btn
                        :label="allSpecialisationsLabel"
                        :to="AppRoute.build(Route.MarketOverview)"
                        color="primary"
                        data-test="landing-specialisations-link"
                        icon-right="sym_r_arrow_forward"
                        no-caps
                        unelevated
                        @click="trackMarketOverview('landing_snapshot')"
                    />
                </div>
                <MarketShareList v-if="leadingSpecialisations.length" :rows="leadingSpecialisations" relative/>
                <q-banner v-else class="unavailable-banner" role="status">
                    <template #avatar>
                        <q-icon color="primary" name="sym_r_cloud_off"/>
                    </template>
                    <strong>Specialisation figures are unavailable right now.</strong>
                    <div class="text-body2 q-mt-xs">The market page and the notes remain available in the meantime.</div>
                </q-banner>
            </div>
        </section>
        <section class="market-section-rule" aria-labelledby="product-boundary-heading">
            <div class="product-boundary-grid container-md q-px-md q-py-xl">
                <div>
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">Two products, one hand-off</p>
                    <h2 id="product-boundary-heading" class="text-h4 text-weight-bold q-mt-none q-mb-md">
                        Xprt Dev explains the market. Xprt Jobs lists the roles.
                    </h2>
                    <p class="text-h6 text-weight-regular text-hint q-my-none">
                        Read the evidence here, then browse the live roles behind it on Xprt Jobs when you are ready.
                    </p>
                </div>
                <div class="product-ledger" role="list" aria-label="Product responsibilities">
                    <div class="ledger-row q-py-md" role="listitem">
                        <span class="font-mono text-caption text-uppercase text-primary">Xprt Dev</span>
                        <span class="text-body1">Market evidence, the notes behind it, My Market and optional career profiles</span>
                    </div>
                    <div class="ledger-row q-py-md" role="listitem">
                        <span class="font-mono text-caption text-uppercase text-primary">Xprt Jobs</span>
                        <span class="text-body1">Job search, job detail, applications and employer tools</span>
                    </div>
                    <q-btn
                        :href="buildXprtJobsCategoryUrl(TECH_MARKET_CLASSIFICATION_SLUG)"
                        class="q-mt-md"
                        color="primary"
                        data-test="landing-jobs-link"
                        icon-right="sym_r_north_east"
                        label="View jobs on Xprt Jobs"
                        no-caps
                        rel="noopener noreferrer"
                        target="_blank"
                        unelevated
                        @click="trackJobsHandoff"
                    />
                </div>
            </div>
        </section>
        <section class="market-section-rule" aria-labelledby="landing-notes-heading">
            <div class="notes-grid container-md q-px-md q-py-xl">
                <div>
                    <p class="font-mono text-caption text-uppercase text-hint q-mb-xs">The notes</p>
                    <h2 id="landing-notes-heading" class="text-h4 text-weight-bold q-mt-none q-mb-md">
                        Know what a number can, and cannot, tell you.
                    </h2>
                    <p class="text-body1 text-hint q-mt-none q-mb-lg">
                        Every figure carries a numbered marker that points to the note explaining how it is counted, where
                        the sample stops and when a value is withheld rather than shown as zero.
                    </p>
                    <q-btn
                        :to="AppRoute.build(Route.Methodology)"
                        color="primary"
                        icon-right="sym_r_arrow_forward"
                        label="Read the notes"
                        no-caps
                        outline
                    />
                </div>
                <ol class="notes-list q-my-none q-pl-none">
                    <li v-for="(note, index) in methodologyNotes" :key="note" class="q-py-sm">
                        <a :href="`/methodology#note-${index + 1}`" class="row items-baseline no-wrap q-gutter-x-md">
                            <span class="font-mono text-caption text-hint">{{ index + 1 }}</span>
                            <span class="text-body1 text-weight-medium">{{ note }}</span>
                        </a>
                    </li>
                </ol>
            </div>
        </section>
        <section aria-label="Account benefits" class="cta-banner" data-test="account-banner">
            <div class="container-md q-px-md q-py-xl">
                <AccountValueSection/>
                <div
                    aria-labelledby="career-profile-heading"
                    class="career-profile-grid cta-rule items-baseline q-mt-xl q-pt-lg"
                    data-test="career-profile-banner"
                    role="region"
                >
                    <p class="font-mono text-caption text-uppercase text-hint q-my-none">Optional account tool</p>
                    <p class="text-body2 q-my-none">
                        <span id="career-profile-heading" class="text-weight-bold">A career profile, when you need one.</span>
                        Keep a professional record beside your market watch and share it through protected links.
                    </p>
                    <q-btn
                        :to="careerProfileRoute"
                        class="career-profile-action"
                        color="white"
                        data-test="career-profile-link"
                        dense
                        flat
                        icon-right="sym_r_arrow_forward"
                        label="Build a career profile"
                        no-caps
                        @click="trackCareerProfileOpen"
                    />
                </div>
            </div>
        </section>
    </main>
</template>

<style lang="scss" scoped>
.landing-page {
    .hero-grid {
        display: grid;
        gap: 48px;
        grid-template-columns: 1fr;
    }

    .hero-copy {
        max-width: 760px;
    }

    .hero-deck {
        max-width: 640px;
    }

    .demand-grid,
    .product-boundary-grid,
    .notes-grid,
    .career-profile-grid {
        display: grid;
        gap: 40px 64px;
        grid-template-columns: minmax(0, 1fr) minmax(280px, 1.2fr);
    }

    .product-boundary-grid,
    .notes-grid {
        grid-template-columns: minmax(0, 1.2fr) minmax(280px, 1fr);
    }

    .unavailable-banner {
        background: rgba($primary, 0.06);
        border: 1px solid rgba($primary, 0.16);
    }

    .career-profile-grid {
        align-items: baseline;
        display: grid;
        gap: 8px 32px;
        grid-template-columns: auto minmax(0, 1fr) auto;
    }

    .notes-list {
        list-style: none;

        li + li {
            border-top: 1px solid rgba($grey-7, 0.16);
        }
    }

    @media (max-width: $breakpoint-sm-max) {
        .demand-grid,
        .product-boundary-grid,
        .notes-grid,
        .career-profile-grid {
            grid-template-columns: 1fr;
        }

        .career-profile-grid {
            gap: 8px;
        }
    }
}
</style>
